import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../api.js';
import { t, getal } from '../i18n.js';
import { St } from './Status.jsx';
import Periodes from './Periodes.jsx';

/**
 * VPN › Site-to-site (docs/besluiten/site-to-site-tunnels.md): de tunnels uit de FMC.
 *
 * Bovenaan een tekening: links onze firewalls, rechts de andere kant, en per tunnel een lijn in de kleur van zijn stand
 * (groen up, rood neer). Over een lijn die up is lopen puntjes, zodat je ziet dat er iets doorheen gaat (niet bij
 * "minder beweging"). Daaronder per tunnel de details en een balk met de laatste zeven dagen.
 */
const STAND = (s) => (s === 'TUNNEL_UP' ? 'good' : s === 'TUNNEL_DOWN' ? 'critical' : 'warning');
const STAND_NAAM = (s) => (s === 'TUNNEL_UP' ? t('up') : s === 'TUNNEL_DOWN' ? t('neer') : t(s.replace(/^TUNNEL_/, '').toLowerCase()));
const kleur = { good: 'var(--st-good)', critical: 'var(--st-critical)', warning: 'var(--st-warning)' };

export default function Tunnels({ klant }) {
  const [alleenNeer, zetAlleenNeer] = useState(false);
  /* De periode van de tijdlijn (24 uur, 7 of 30 dagen) staat in de adresbalk, naast de tab. */
  const [zoek, zetZoek] = useSearchParams();
  const gekozen = [1, 7, 30].includes(Number(zoek.get('dagen'))) ? Number(zoek.get('dagen')) : 7;
  const kiesDagen = (d) => zetZoek((z) => { const n = new URLSearchParams(z); if (d === 7) n.delete('dagen'); else n.set('dagen', String(d)); return n; });
  const q = useQuery({ queryKey: ['tunnels', klant, gekozen], queryFn: () => api.tunnels(klant, gekozen), refetchInterval: 60_000,
    /* Vorige data alleen vasthouden als de klant gelijk bleef (period-wissel vloeiend); bij een klantwissel niet,
       anders blijft de vorige klant in beeld staan tot de nieuwe binnen is. */
    placeholderData: (vorige, q) => (q?.queryKey?.[1] === klant ? vorige : undefined) });
  if (q.isPending) return <p className="muted">{t('Laden…')}</p>;
  if (q.isError) return <div className="melder rood">{t(q.error.message)}</div>;
  const { tunnels, dagen } = q.data.data;
  const deLaatste = dagen === 1 ? t('de laatste 24 uur') : t('de laatste {n} dagen', { n: dagen });
  if (!tunnels.length) {
    return <section className="card" style={{ marginBottom: 18 }}><header><h2>{t('Site-to-site-tunnels')}</h2></header><p className="empty">{t('Nog geen site-to-site-tunnels. ADTS ZERO leest ze elke twee minuten uit de FMC (met de leesgebruiker onder Bronnen › FMC › API).')}</p></section>;
  }
  const neer = tunnels.filter((x) => x.state !== 'TUNNEL_UP').length;
  /* Bij veel tunnels: alleen wat neer is, met één klik. */
  const toon = alleenNeer ? tunnels.filter((x) => x.state !== 'TUNNEL_UP') : tunnels;
  return (
    <>
      <div className="filters"><Periodes keuzes={[1, 7, 30]} waarde={gekozen} onKies={kiesDagen} /></div>
      <div className="tiles">
        <div className="tile"><div className="n">{getal(tunnels.length)}</div><div className="t">{t('tunnels')}</div></div>
        <div className="tile"><div className="n">{getal(tunnels.length - neer)}</div><div className="t">{t('up')}</div></div>
        <div className={`tile ${neer ? 'crit' : ''}`}><div className="n">{getal(neer)}</div><div className="t">{t('neer')}</div></div>
        <div className="tile"><div className="n">{getal(tunnels.reduce((n, x) => n + x.syslog.up, 0))}</div><div className="t">{t('rekeys, 24 uur')}</div></div>
      </div>
      <section className="card" style={{ marginBottom: 18 }}>
        <header><h2>{t('Site-to-site-tunnels')}</h2>
          <span className="meta legenda"><span><i style={{ background: kleur.good }} />{t('up')}</span><span><i style={{ background: kleur.critical }} />{t('neer')}</span></span></header>
        {tunnels.length > 1 && (
          <div className="filters" style={{ marginBottom: 8 }}>
            <button type="button" className="chip" aria-pressed={!alleenNeer} onClick={() => zetAlleenNeer(false)}>{t('Alle ({n})', { n: tunnels.length })}</button>
            <button type="button" className="chip" aria-pressed={alleenNeer} onClick={() => zetAlleenNeer(true)}>{t('Alleen neer ({n})', { n: neer })}</button>
          </div>
        )}
        {toon.length ? <Tekening tunnels={toon} klant={klant} /> : <p className="empty"><St soort="good">{t('Alle tunnels zijn up.')}</St></p>}
      </section>
      <section className="card" style={{ marginBottom: 18 }}>
        <header><h2>{t('Tunnels')}</h2><span className="meta">{deLaatste}</span></header>
        <div className="tunnellijst compact">{[...toon].sort((a, b) => (a.state === 'TUNNEL_UP') - (b.state === 'TUNNEL_UP'))
          .map((x) => <Tunnel key={x.id} x={x} dagen={dagen} />)}</div>
        <p className="noot">{t('De stand komt uit de FMC (elke twee minuten); rekeys en mislukte onderhandelingen uit de syslog van de firewall.')}</p>
      </section>
    </>
  );
}

/* Iconen in de tekening (dezelfde lijnen als iconen.jsx, hier als paden binnen de svg). */
const ICOON_FW = 'M3 5h18v14H3zM3 9.7h18M3 14.3h18M9 5v4.7M15 5v4.7M6 9.7v4.6M12 9.7v4.6M18 9.7v4.6M9 14.3V19M15 14.3V19';
const ICOON_WOLK = 'M7 18.5h10.5a4 4 0 0 0 .5-7.97A6 6 0 0 0 6.3 9.3 4.6 4.6 0 0 0 7 18.5z';
const ICOON_WERELD = 'M3.5 12a8.5 8.5 0 1 0 17 0a8.5 8.5 0 1 0-17 0M3.5 12h17M12 3.5c2.6 2.6 2.6 14.4 0 17M12 3.5c-2.6 2.6-2.6 14.4 0 17';
/* Cisco Umbrella SIG staat in 146.112.0.0/16: dan zeggen we het erbij. */
const isUmbrella = (k) => /^146\.112\./.test(k.ip ?? '');
/* De andere kant: een eigen firewall, Umbrella (wolk) of een externe partij (wereld). */
export const peerIcoon = (k) => (k.type === 'FTD' ? ICOON_FW : isUmbrella(k) ? ICOON_WOLK : ICOON_WERELD);
const watIs = (k) => (k.type === 'FTD' ? t('firewall') : isUmbrella(k) ? 'Cisco Umbrella SIG' : t('externe partij'));
const duur = (ms) => {
  const m = Math.max(0, Math.round(ms / 60_000));
  if (m < 60) return t('{n} min', { n: m });
  if (m < 48 * 60) return t('{n} uur', { n: Math.round(m / 60) });
  return t('{n} dagen', { n: Math.round(m / 1440) });
};

/** De tekening op de breedte van de kaart: het samengevoegde schema (Netwerk hieronder). */
function Tekening({ tunnels, klant }) {
  const doos = useRef(null);
  const W = useBreedte(doos);
  return <div ref={doos} className="tunneltekening"><Netwerk tunnels={tunnels} W={W} klant={klant} /></div>;
}

/**
 * De tunnels als **ster/krachten-schema** (Bernhard, 2026-10-30): elk eindpunt (onze firewall of een
 * bestemming) is één rondje, verspreid over de pagina. Verbonden knopen trekken naar elkaar en alle
 * knopen duwen elkaar weg (een krachtenlayout), zodat gedeelde bestemmingen vanzelf centraal komen met
 * hun verbindingen eromheen — een sterstructuur met zo min mogelijk kruisende lijnen. Per tunnel één
 * gekleurde lijn (groen up, rood neer, oranje onbekend). De plaatsing ligt vast per topologie (niet bij
 * elke verversing opnieuw). Hover over een lijn: de tunnel; over een rondje: de firewall of bestemming.
 */
const som = (lijst, k) => { const s = lijst.map((x) => x.stats).filter(Boolean);
  return s.some((x) => x[k] !== null && x[k] !== undefined) ? s.reduce((n, x) => n + (x[k] ?? 0), 0) : null; };

/* Een krachtenlayout (Fruchterman–Reingold) in een vaste virtuele doos, deterministisch per topologie
   (zelfde invoer → zelfde plaatje). Verbonden knopen veren samen, alle knopen stoten af, en een zachte
   kracht naar het midden houdt het bijeen. */
function sterLayout(nodes, edges, VW, VH) {
  const n = nodes.length;
  if (!n) return [];
  let seed = 7;
  for (const nd of nodes) for (const c of String(nd.key)) seed = (seed * 31 + c.charCodeAt(0)) >>> 0;
  const rng = () => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 4294967296; };
  const idx = new Map(nodes.map((nd, i) => [nd.key, i]));
  const buren = nodes.map(() => []);
  for (const e of edges) {
    const a = idx.get(e.a); const b = idx.get(e.b);
    if (a !== undefined && b !== undefined && a !== b) { buren[a].push(b); buren[b].push(a); }
  }
  /* Losse clusters (klanten die niet met elkaar verbonden zijn) apart houden: elk cluster krijgt zijn
     eigen vak in een raster, zodat ze niet over elkaar heen vallen (bv. in "Alle klanten"). */
  const cluster = new Array(n).fill(-1); const clusters = [];
  for (let i = 0; i < n; i += 1) {
    if (cluster[i] >= 0) continue;
    const id = clusters.length; const leden = [i]; cluster[i] = id; const rij = [i];
    while (rij.length) { const u = rij.pop(); for (const v of buren[u]) if (cluster[v] < 0) { cluster[v] = id; leden.push(v); rij.push(v); } }
    clusters.push(leden);
  }
  const kolommen = Math.ceil(Math.sqrt(clusters.length));
  const rijen = Math.ceil(clusters.length / kolommen);
  const vw = VW / kolommen; const vh = VH / rijen;
  const p = nodes.map(() => ({ x: 0, y: 0, vx: 0, vy: 0 }));
  clusters.forEach((leden, ci) => {
    const ox = (ci % kolommen) * vw; const oy = Math.floor(ci / kolommen) * vh;
    const m = leden.length;
    for (const i of leden) { p[i].x = ox + 26 + rng() * (vw - 52); p[i].y = oy + 26 + rng() * (vh - 52); }
    const k = 0.8 * Math.sqrt((vw * vh) / m);
    for (let stap = 0; stap < 320; stap += 1) {
      const koel = 1 - stap / 320;
      for (let a = 0; a < m; a += 1) for (let b = a + 1; b < m; b += 1) {
        const i = leden[a]; const j = leden[b];
        let dx = p[i].x - p[j].x; let dy = p[i].y - p[j].y; const d = Math.hypot(dx, dy) || 0.01;
        const f = (k * k) / d; dx /= d; dy /= d;
        p[i].vx += dx * f; p[i].vy += dy * f; p[j].vx -= dx * f; p[j].vy -= dy * f;
      }
      for (const i of leden) for (const j of buren[i]) {
        if (j <= i) continue;
        let dx = p[i].x - p[j].x; let dy = p[i].y - p[j].y; const d = Math.hypot(dx, dy) || 0.01;
        const f = (d * d) / k; dx /= d; dy /= d;
        p[i].vx -= dx * f; p[i].vy -= dy * f; p[j].vx += dx * f; p[j].vy += dy * f;
      }
      const mx = ox + vw / 2; const my = oy + vh / 2;
      for (const i of leden) { p[i].vx += (mx - p[i].x) * 0.04; p[i].vy += (my - p[i].y) * 0.04; }
      const max = 40 * koel + 2;
      for (const i of leden) {
        const vl = Math.hypot(p[i].vx, p[i].vy) || 1; const mm = Math.min(vl, max);
        p[i].x += (p[i].vx / vl) * mm; p[i].y += (p[i].vy / vl) * mm; p[i].vx = 0; p[i].vy = 0;
        p[i].x = Math.max(ox + 22, Math.min(ox + vw - 22, p[i].x)); p[i].y = Math.max(oy + 22, Math.min(oy + vh - 22, p[i].y));
      }
    }
  });
  return p;
}

function Netwerk({ tunnels, W, klant, adressen = true }) {
  const [tip, zetTip] = useState(null);
  const [sleep, zetSleep] = useState(null);
  const svgRef = useRef(null);
  const nu = Date.now();
  const sleutel = (k) => k.name ?? k.ip;

  /* Eén knoop per eindpunt (firewall of bestemming), samengevoegd; de tunnels als lijnen ertussen. */
  const { nodes, edges } = useMemo(() => {
    const m = new Map();
    const raak = (k, x, ours) => {
      const s = sleutel(k);
      const g = m.get(s) ?? { key: s, name: k.name ?? k.ip, ip: null, type: k.type ?? null, iface: k.interface ?? null, ours: false, info: null, lijst: [] };
      if (ours) { g.ours = true; g.info = g.info ?? x.firewall ?? null; }
      if (k.ip && !g.ip) g.ip = k.ip;
      if (k.type && !g.type) g.type = k.type;
      g.lijst.push(x); m.set(s, g);
      return g;
    };
    for (const x of tunnels) { raak(x.local, x, true); raak(x.peer, x, false); }
    const nodes = [...m.values()].map((g) => ({ ...g, neer: g.lijst.filter((x) => x.state !== 'TUNNEL_UP').length }));
    const edges = tunnels.map((x) => ({ a: sleutel(x.local), b: sleutel(x.peer), x }));
    return { nodes, edges };
  }, [tunnels]);

  /* De plaatsing: vast per topologie (de verzameling knopen en verbindingen), niet per verversing of stand. */
  const topo = useMemo(() => `${nodes.map((x) => x.key).sort().join('|')}#${edges.map((e) => `${e.a}>${e.b}`).sort().join('|')}`, [nodes, edges]);
  const VW = 1000; const VH = 620;
  const basis = useMemo(() => sterLayout(nodes, edges, VW, VH), [topo]); // eslint-disable-line react-hooks/exhaustive-deps
  const H = Math.round(Math.min(680, Math.max(380, W * 0.6)));
  const basisMap = new Map(nodes.map((nd, i) => [nd.key, basis[i] ?? { x: VW / 2, y: VH / 2 }]));

  /* Zelf gesleepte posities (virtueel), per klant in deze browser bewaard. Een node die je versleept blijft
     staan; de rest volgt de krachtenlayout. De schikking wordt automatisch onthouden (geen aparte knop). */
  const lsSleutel = `adts-zero-vpn-layout:${klant ?? 'alle'}`;
  const [eigen, zetEigen] = useState({});
  /* Stabiel per topologie (niet per data-refetch), anders zou het herstel-effect elke 60 s je gesleepte
     posities opnieuw inlezen en zou het schema steeds "verspringen". */
  const sleutels = useMemo(() => new Set(nodes.map((nd) => nd.key)), [topo]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    let op = {};
    try {
      const r = JSON.parse(localStorage.getItem(lsSleutel) || 'null');
      if (r && r.pos) op = Object.fromEntries(Object.entries(r.pos).filter(([k]) => sleutels.has(k)));
    } catch { /* geen opslag */ }
    zetEigen(op);
  }, [lsSleutel, sleutels]);
  /* Auto-opslaan: zodra een versleping klaar is (niet meer bezig), de schikking wegschrijven. */
  useEffect(() => {
    if (sleep) return;
    try {
      if (Object.keys(eigen).length) localStorage.setItem(lsSleutel, JSON.stringify({ pos: eigen }));
      else localStorage.removeItem(lsSleutel);
    } catch { /* geen opslag */ }
  }, [eigen, sleep, lsSleutel]);
  const vpos = (key) => eigen[key] ?? basisMap.get(key) ?? { x: VW / 2, y: VH / 2 };
  const sx = (key) => vpos(key).x * (W / VW);
  const sy = (key) => vpos(key).y * (H / VH);
  const naarVirtueel = (e) => {
    const r = svgRef.current.getBoundingClientRect();
    return { x: Math.max(8, Math.min(W - 8, e.clientX - r.left)) * (VW / W), y: Math.max(8, Math.min(H - 8, e.clientY - r.top)) * (VH / H) };
  };
  const beweeg = (e) => { if (!sleep) return; const v = naarVirtueel(e); zetEigen((p) => ({ ...p, [sleep]: v })); };
  const herschik = () => { zetEigen({}); };
  const aangepast = Object.keys(eigen).length > 0;

  const fwTip = (g) => ({ titel: g.name, regels: [
    [t('Model'), [g.info?.model, g.info?.version].filter(Boolean).join(' · ') || '—'],
    adressen ? [t('Adres'), [g.ip, g.iface].filter(Boolean).join(' · ') || '—'] : null,
    [t('Tunnels'), t('{n} up, {m} neer', { n: g.lijst.length - g.neer, m: g.neer })],
    [t('Bytes verstuurd / ontvangen'), `${bytes(som(g.lijst, 'bytesTx'))} / ${bytes(som(g.lijst, 'bytesRx'))}`],
  ].filter(Boolean) });
  const peerTip = (g) => ({ titel: g.name, regels: [
    adressen && g.ip && g.ip !== g.name ? [t('Adres'), g.ip] : null,
    [t('Soort'), watIs(g)],
    [t('Tunnels'), t('{n} up, {m} neer', { n: g.lijst.length - g.neer, m: g.neer })],
  ].filter(Boolean) });
  const tunnelTip = (x) => { const s = x.stats ?? {}; return { titel: x.topology, regels: [
    [t('Stand'), `${STAND_NAAM(x.state)}${x.since ? ` · ${duur(nu - new Date(x.since).getTime())}` : ''}`],
    [t('Andere kant'), [x.peer.name, adressen ? (s.peerIp ?? x.peer.ip) : null, watIs(x.peer)].filter(Boolean).join(' · ')],
    [t('Soort'), x.local.vti?.name ? `VTI ${x.local.vti.name}` : x.routeBased ? t('route-based') : t('policy-based')],
    s.encryption ? [t('Versleuteling'), s.encryption] : null,
    [t('Pakketten versleuteld / ontsleuteld'), `${kortGetal(s.encaps)} / ${kortGetal(s.decaps)}`],
    [t('Bytes verstuurd / ontvangen'), `${bytes(s.bytesTx)} / ${bytes(s.bytesRx)}`],
  ].filter(Boolean) }; };
  const toonTip = (e, inhoud) => {
    const doos = e.currentTarget.ownerSVGElement.getBoundingClientRect();
    zetTip({ ...inhoud, x: e.clientX - doos.left, y: e.clientY - doos.top });
  };

  return (
    <div className="tk-boom tk-ster" style={{ position: 'relative' }} onMouseLeave={() => zetTip(null)}>
      <div className="filters" style={{ marginBottom: 8, alignItems: 'center' }}>
        <span className="muted klein" style={{ marginRight: 'auto' }}>{t('Sleep de rondjes om de kaart te ordenen; je schikking wordt onthouden in deze browser.')}</span>
        {aangepast && <button type="button" className="chip" onClick={herschik}>{t('Automatisch schikken')}</button>}
      </div>
      <svg ref={svgRef} width={W} height={H} role="img" style={{ touchAction: 'none' }}
        onPointerMove={beweeg} onPointerUp={() => zetSleep(null)} onPointerLeave={() => zetSleep(null)}
        aria-label={t('Site-to-site-tunnels: {n} tunnels', { n: tunnels.length })}>
        <defs>
          <linearGradient id="tk-ons" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="var(--st-good)" /><stop offset="1" stopColor="#0a7d0a" />
          </linearGradient>
          <filter id="tk-schaduw" x="-30%" y="-40%" width="160%" height="200%">
            <feDropShadow dx="0" dy="1.5" stdDeviation="2" floodColor="#07141f" floodOpacity="0.22" />
          </filter>
        </defs>
        {edges.map((e) => {
          const s = STAND(e.x.state);
          return (
            <g key={`e-${e.x.id}`} className="tk-tunnel" onMouseMove={(ev) => toonTip(ev, tunnelTip(e.x))} onMouseLeave={() => zetTip(null)}>
              <line x1={sx(e.a)} y1={sy(e.a)} x2={sx(e.b)} y2={sy(e.b)} stroke="transparent" strokeWidth="12" />
              <line x1={sx(e.a)} y1={sy(e.a)} x2={sx(e.b)} y2={sy(e.b)} className={`tk-lijn ${s}`} style={{ stroke: kleur[s] }} />
              {/* Een up-tunnel: puntjes die langs de lijn lopen, zodat je ziet dat er verkeer doorheen gaat. */}
              {s === 'good' && <line x1={sx(e.a)} y1={sy(e.a)} x2={sx(e.b)} y2={sy(e.b)} className="tk-stroom" />}
            </g>
          );
        })}
        {nodes.map((nd) => {
          const px = sx(nd.key); const py = sy(nd.key);
          /* De naam van het HA-paar zonder de actieve-unit tussen [haken] (die staat in de hover); past netjes. */
          const label = kort(nd.name.replace(/\s*\[[^\]]*\]\s*$/, ''), 26);
          const sub = nd.neer ? t('{n} neer', { n: nd.neer }) : t(nd.lijst.length === 1 ? '1 tunnel' : '{n} tunnels', { n: nd.lijst.length });
          const tekstBreed = Math.max(label.length, sub.length + 2) * 7.1;
          const IX = 42; const PAD = 16; /* ruimte voor icoon links, marge rechts */
          const w = Math.round(Math.max(120, IX + tekstBreed + PAD));
          const h = 40;
          return (
            <g key={`n-${nd.key}`} className={`tk-blok ${nd.ours ? 'ons' : 'peer'}${nd.neer ? ' neer' : ''}`}
              style={{ cursor: sleep === nd.key ? 'grabbing' : 'grab' }} transform={`translate(${px}, ${py})`}
              onPointerDown={(e) => { e.preventDefault(); zetTip(null); zetSleep(nd.key); }}
              onMouseMove={(e) => { if (!sleep) toonTip(e, nd.ours ? fwTip(nd) : peerTip(nd)); }} onMouseLeave={() => zetTip(null)}>
              <rect x={-w / 2} y={-h / 2} width={w} height={h} rx="11" className="tk-blok-vak" filter="url(#tk-schaduw)" />
              <rect x={-w / 2} y={-h / 2} width="5" height={h} className="tk-blok-accent" />
              <g transform={`translate(${-w / 2 + 14}, -12)`} className={`tk-icoon${nd.ours ? '' : ' peer'}`}><path d={nd.ours ? ICOON_FW : peerIcoon(nd)} /></g>
              <text x={-w / 2 + IX} y="-2" className="tk-blok-naam">{label}</text>
              <text x={-w / 2 + IX} y="13" className="tk-blok-sub">{sub}</text>
            </g>
          );
        })}
      </svg>
      {tip && (
        <div className="tk-tip" role="tooltip" style={{ left: tip.x + 350 > W ? Math.max(0, tip.x - 344) : tip.x + 14,
          ...(tip.y + 40 + tip.regels.length * 20 > H ? { bottom: H - tip.y + 10 } : { top: tip.y + 14 }) }}>
          <b>{tip.titel}</b>
          <dl>{tip.regels.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>
        </div>
      )}
    </div>
  );
}

/** De breedte van een element, bijgehouden bij elke verandering. */
function useBreedte(ref) {
  const [W, zetW] = useState(900);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => zetW(Math.max(260, el.clientWidth)));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return W;
}

/**
 * Het overzicht: de tunnels van de gekozen klant als boom, niet hoger dan 340 px, met een link naar VPN › Site-to-site.
 * Zonder tunnels niets.
 */
export function TunnelsOverzicht({ klant }) {
  const doos = useRef(null);
  const W = useBreedte(doos);
  const q = useQuery({ queryKey: ['tunnels', klant], queryFn: () => api.tunnels(klant), refetchInterval: 60_000 });
  const lijst = q.data?.data.tunnels ?? [];
  const neer = lijst.filter((x) => x.state !== 'TUNNEL_UP').length;
  return (
    <section className="card" style={{ marginBottom: 18, display: lijst.length ? undefined : 'none' }}>
      <header><h2>{t('Site-to-site-tunnels')}</h2>
        <span className="meta">{neer ? <St soort="critical">{t(neer > 1 ? '{n} tunnels neer' : '{n} tunnel neer', { n: neer })}</St>
          : t('alle {n} up', { n: lijst.length })} · <Link to="/vpn?tab=s2s">{t('alle tunnels')}</Link></span></header>
      <div ref={doos} className="tunneltekening">{lijst.length > 0 && <Compact tunnels={lijst} dagen={q.data.data.dagen} W={W} hoogte={340} />}</div>
    </section>
  );
}

/* Een getal kort: 20.882.493 wordt 20,9 mln; bytes als kB, MB of GB. */
const kortGetal = (n) => (n === null || n === undefined ? '—' : n >= 1e6 ? `${getal(Math.round(n / 1e5) / 10)} mln` : n >= 1e4 ? `${getal(Math.round(n / 100) / 10)}k` : getal(n));
const bytes = (n) => (n === null || n === undefined ? '—' : n >= 1e9 ? `${getal(Math.round(n / 1e8) / 10)} GB` : n >= 1e6 ? `${getal(Math.round(n / 1e5) / 10)} MB` : n >= 1e3 ? `${getal(Math.round(n / 100) / 10)} kB` : `${getal(n)} B`);

/**
 * De tekening (Bernhard, 2026-10-26: "meer uit elkaar, per tunnel een groene lijn"): per firewall een blok, de firewall
 * in het midden, zijn tunnels eronder in een rij (of meer rijen) met ruimte ertussen, en elke tunnel met een eigen lijn
 * vanaf de firewall. Lijn en kaartje in de kleur van de stand: groen up, rood neer. Wat neer is staat vooraan; een
 * firewall met een tunnel neer komt eerst.
 *
 * Over de firewall met de muis: wat ZERO van hem weet (model, versie, beheer, tunnels, pakketten en bytes van zijn
 * tunnels); over een tunnel: zijn eigen tellers en versleuteling (uit show-commando's via de FMC, elke vijf minuten).
 *
 * Met `hoogte` (SOC-scherm, overzicht) kleiner: één regel per kaartje, en past het nog niet, dan wat neer is en
 * "+ N tunnels up". `adressen`: geen adressen op een wandscherm.
 */
export function Compact({ tunnels, dagen, W, hoogte = null, adressen = true }) {
  const [tip, zetTip] = useState(null);
  const sleutel = (k) => k.name ?? k.ip;
  const nu = Date.now();
  const opVolgorde = (a, b) => (a.state === 'TUNNEL_UP') - (b.state === 'TUNNEL_UP') || a.topology.localeCompare(b.topology);
  const groepen = [...tunnels.reduce((m, x) => m.set(sleutel(x.local), [...(m.get(sleutel(x.local)) ?? []), x]), new Map())]
    .map(([naam, lijst]) => ({ naam, fw: lijst[0].local, info: lijst[0].firewall ?? null, lijst: lijst.sort(opVolgorde),
      neer: lijst.filter((x) => x.state !== 'TUNNEL_UP').length }))
    .sort((a, b) => (b.neer > 0) - (a.neer > 0) || a.naam.localeCompare(b.naam));

  const klein = Boolean(hoogte);
  const FW = { w: Math.min(W - 20, klein ? 230 : 280), h: klein ? 40 : 52 };
  const KAART = { w: klein ? 170 : 236, h: klein ? 30 : 74 };
  const GAT = klein ? 14 : 28;
  const LIJN = klein ? 40 : 64;
  const perRij = Math.max(1, Math.floor((W - 20 + GAT) / (KAART.w + GAT)));
  /* Met een vaste hoogte: hoeveel rijen kaartjes er passen, over alle firewalls samen. */
  const rijenNodig = groepen.reduce((n, g) => n + Math.ceil(g.lijst.length / perRij), 0);
  const ruimte = klein ? Math.floor((hoogte - groepen.length * (FW.h + LIJN + 12)) / (KAART.h + 10)) : Infinity;
  const teVeel = klein && rijenNodig > Math.max(groepen.length, ruimte);

  let y = 6;
  const blokken = groepen.map((g) => {
    /* Past het niet: per firewall één rij, wat neer is eerst, en de rest als "+ N". */
    const toon = teVeel ? g.lijst.slice(0, Math.max(1, perRij - 1)) : g.lijst;
    const rest = g.lijst.slice(toon.length);
    const n = toon.length + (rest.length ? 1 : 0);
    const kolommen = Math.min(perRij, n);
    const rijen = Math.ceil(n / perRij);
    const breed = kolommen * KAART.w + (kolommen - 1) * GAT;
    const fwY = y;
    const kaartY = fwY + FW.h + LIJN;
    const plekken = toon.map((x, i) => {
      const r = Math.floor(i / perRij);
      const inRij = r === rijen - 1 ? n - r * perRij : perRij;
      const rijBreed = inRij * KAART.w + (inRij - 1) * GAT;
      return { x, kx: W / 2 - rijBreed / 2 + (i % perRij) * (KAART.w + GAT), ky: kaartY + r * (KAART.h + 18) };
    });
    const restPlek = rest.length ? (() => {
      const i = toon.length; const r = Math.floor(i / perRij);
      const inRij = r === rijen - 1 ? n - r * perRij : perRij;
      const rijBreed = inRij * KAART.w + (inRij - 1) * GAT;
      return { kx: W / 2 - rijBreed / 2 + (i % perRij) * (KAART.w + GAT), ky: kaartY + r * (KAART.h + 18) };
    })() : null;
    y = kaartY + rijen * (KAART.h + 18) + (klein ? 6 : 22);
    return { g, fwY, plekken, rest, restPlek, breed };
  });
  const H = y;

  const fwTip = (g) => {
    const s = g.lijst.map((x) => x.stats).filter(Boolean);
    const som = (k) => (s.some((x) => x[k] !== null && x[k] !== undefined) ? s.reduce((n, x) => n + (x[k] ?? 0), 0) : null);
    return { titel: g.fw.name ?? g.fw.ip, regels: [
      [t('Model'), [g.info?.model, g.info?.version].filter(Boolean).join(' · ') || '—'],
      adressen ? [t('Adres'), [g.fw.ip, g.fw.interface].filter(Boolean).join(' · ') || '—'] : null,
      adressen && g.info?.beheer ? [t('Beheer'), g.info.beheer] : null,
      [t('Tunnels'), t('{n} up, {m} neer', { n: g.lijst.length - g.neer, m: g.neer })],
      [t('Pakketten versleuteld'), kortGetal(som('encaps'))],
      [t('Pakketten ontsleuteld'), kortGetal(som('decaps'))],
      [t('Bytes verstuurd / ontvangen'), `${bytes(som('bytesTx'))} / ${bytes(som('bytesRx'))}`],
    ].filter(Boolean) };
  };
  const tunnelTip = (x) => {
    const s = x.stats ?? {};
    return { titel: x.topology, regels: [
      [t('Stand'), `${STAND_NAAM(x.state)}${x.since ? ` · ${duur(nu - new Date(x.since).getTime())}` : ''}`],
      [t('Andere kant'), [x.peer.name, adressen ? (s.peerIp ?? x.peer.ip) : null, watIs(x.peer)].filter(Boolean).join(' · ')],
      [t('Soort'), x.local.vti?.name ? `VTI ${x.local.vti.name}` : x.routeBased ? t('route-based') : t('policy-based')],
      s.encryption ? [t('Versleuteling'), s.encryption] : null,
      [t('Pakketten versleuteld / ontsleuteld'), `${kortGetal(s.encaps)} / ${kortGetal(s.decaps)}`],
      [t('Bytes verstuurd / ontvangen'), `${bytes(s.bytesTx)} / ${bytes(s.bytesRx)}`],
      s.duration ? [t('Verbonden'), s.duration] : null,
      s.sendErrors || s.recvErrors ? [t('Fouten'), `${getal(s.sendErrors ?? 0)} / ${getal(s.recvErrors ?? 0)}`] : null,
    ].filter(Boolean) };
  };
  const toonTip = (e, inhoud) => {
    const doos = e.currentTarget.ownerSVGElement.getBoundingClientRect();
    zetTip({ ...inhoud, x: e.clientX - doos.left, y: e.clientY - doos.top });
  };

  return (
    <div className="tk-boom" style={{ position: 'relative' }} onMouseLeave={() => zetTip(null)}>
      <svg width={W} height={H} role="img" aria-label={t('Site-to-site-tunnels: {n} tunnels', { n: tunnels.length })}>
        {blokken.map(({ g, fwY, plekken, rest, restPlek }) => {
          const fx = W / 2 - FW.w / 2;
          const onder = fwY + FW.h;
          return (
            <g key={g.naam}>
              {plekken.map(({ x, kx, ky }, i) => {
                const s = STAND(x.state);
                /* Elke tunnel een eigen lijn: vanaf een eigen punt onder de firewall naar het midden van zijn kaartje. */
                const start = W / 2 + (plekken.length > 1 ? (i / (plekken.length - 1) - 0.5) * (FW.w - 40) : 0);
                const doel = kx + KAART.w / 2;
                const pad = `M${start},${onder} C${start},${onder + LIJN * 0.6} ${doel},${ky - LIJN * 0.5} ${doel},${ky}`;
                return (
                  <g key={x.id}>
                    <path d={pad} className={`tk-lijn ${s}`} style={{ stroke: kleur[s] }} />
                    {s === 'good' && <path d={pad} className="tk-stroom" />}
                  </g>
                );
              })}
              <g className="tk-fw" onMouseMove={(e) => toonTip(e, fwTip(g))} onMouseLeave={() => zetTip(null)}>
                <rect x={fx} y={fwY} width={FW.w} height={FW.h} rx="12" className="tk-knoop" />
                <g transform={`translate(${fx + 12}, ${fwY + FW.h / 2 - 12})`} className="tk-icoon"><path d={ICOON_FW} /></g>
                <text x={fx + 46} y={fwY + (klein ? 17 : 22)} className="tk-naam">{kort(g.fw.name ?? g.fw.ip, Math.floor((FW.w - 56) / 7.4))}</text>
                <text x={fx + 46} y={fwY + (klein ? 31 : 38)} className="tk-sub">{[adressen && !klein ? g.fw.ip : null,
                  t(g.lijst.length === 1 ? '1 tunnel' : '{n} tunnels', { n: g.lijst.length }), g.neer ? t('{n} neer', { n: g.neer }) : null].filter(Boolean).join(' · ')}</text>
              </g>
              {plekken.map(({ x, kx, ky }) => {
                const s = STAND(x.state);
                const st = x.stats;
                const peer = [x.peer.name ?? x.peer.ip, adressen && x.peer.name && x.peer.ip !== x.peer.name ? x.peer.ip : null].filter(Boolean).join(' · ');
                return (
                  <g key={`k-${x.id}`} className="tk-tunnel" onMouseMove={(e) => toonTip(e, tunnelTip(x))} onMouseLeave={() => zetTip(null)}>
                    <rect x={kx} y={ky} width={KAART.w} height={KAART.h} rx="10" className={`tk-peer ${s}`} />
                    {/* Klein: het icoon van de andere kant in de kleur van de stand, in plaats van de stip. */}
                    {klein
                      ? <g transform={`translate(${kx + 6}, ${ky + KAART.h / 2 - 9}) scale(0.75)`} className="tk-icoon" style={{ stroke: kleur[s] }}><path d={peerIcoon(x.peer)} /></g>
                      : <circle cx={kx + 13} cy={ky + 17} r="4.5" style={{ fill: kleur[s] }} />}
                    {!klein && <g transform={`translate(${kx + KAART.w - 32}, ${ky + 8})`} className="tk-icoon peer"><path d={peerIcoon(x.peer)} /></g>}
                    <text x={kx + (klein ? 28 : 24)} y={ky + (klein ? KAART.h / 2 + 4 : 21)} className="tk-titel">{kort(x.topology, Math.floor((KAART.w - (klein ? (s !== 'good' ? 74 : 38) : 62)) / 7.4))}</text>
                    {klein && s !== 'good' && x.since && <text x={kx + KAART.w - 8} y={ky + KAART.h / 2 + 4} textAnchor="end" className="tk-regel">{duur(nu - new Date(x.since).getTime())}</text>}
                    {!klein && <>
                      <text x={kx + 12} y={ky + 39} className="tk-regel">{kort(peer, Math.floor((KAART.w - 20) / 6.4))}</text>
                      <text x={kx + 12} y={ky + 57} className="tk-regel zacht">{`${STAND_NAAM(x.state)}${x.since ? ` · ${duur(nu - new Date(x.since).getTime())}` : ''}`}</text>
                      {st && (st.encaps !== null || st.decaps !== null) && (
                        <text x={kx + KAART.w - 10} y={ky + 57} textAnchor="end" className="tk-regel zacht">{`↑ ${kortGetal(st.encaps)}  ↓ ${kortGetal(st.decaps)}`}</text>
                      )}
                    </>}
                  </g>
                );
              })}
              {restPlek && (
                <text x={restPlek.kx + KAART.w / 2} y={restPlek.ky + KAART.h / 2 + 4} textAnchor="middle" className="tk-regel zacht">
                  {rest.every((x) => x.state === 'TUNNEL_UP') ? t('+ {n} tunnels up', { n: rest.length }) : t('+ {n} tunnels', { n: rest.length })}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      {tip && (
        /* Rechts onder de muis; past dat niet, dan links of erboven. */
        <div className="tk-tip" role="tooltip" style={{ left: tip.x + 350 > W ? Math.max(0, tip.x - 344) : tip.x + 14,
          ...(tip.y + 40 + tip.regels.length * 20 > H ? { bottom: H - tip.y + 10 } : { top: tip.y + 14 }) }}>
          <b>{tip.titel}</b>
          <dl>{tip.regels.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>
        </div>
      )}
    </div>
  );
}

/** De stukken van de balk door de tijd en het percentage up, uit de wissels die ZERO zag; null als er nog niets gemeten is. */
function uptijd(x, dagen) {
  const eind = Date.now();
  const begin = eind - dagen * 86_400_000;
  const stukken = [];
  let van = begin;
  let stand = x.before;
  /* De FMC zegt sinds wanneer de tunnel in zijn huidige stand staat: vanaf dan weten we het, ook vóór ZERO meekeek. */
  const wissels = [...(x.changes ?? [])];
  const sinds = x.since ? new Date(x.since).getTime() : null;
  if (sinds && !wissels.some((w) => Math.abs(new Date(w.at).getTime() - sinds) < 60_000 || new Date(w.at).getTime() > sinds)) {
    wissels.push({ at: new Date(sinds).toISOString(), state: x.state });
    wissels.sort((p, q) => new Date(p.at) - new Date(q.at));
  }
  for (const w of wissels) {
    const at = Math.max(begin, new Date(w.at).getTime());
    if (at > van) stukken.push({ van, tot: at, stand });
    van = at; stand = w.state;
  }
  stukken.push({ van, tot: eind, stand });
  const op = stukken.filter((s) => s.stand === 'TUNNEL_UP').reduce((n, s) => n + (s.tot - s.van), 0);
  const gemeten = stukken.filter((s) => s.stand).reduce((n, s) => n + (s.tot - s.van), 0);
  return { stukken, begin, eind, pct: gemeten > 0 ? Math.round((op / gemeten) * 1000) / 10 : null };
}
/* Het begin van elke dag (middernacht in Nederland) binnen de periode; bij 24 uur elke zes uur. */
function dagGrenzen(begin, eind, dagen) {
  const uit = [];
  const d = new Date(begin);
  const fmt = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Amsterdam', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  const streep = dagen === 1 ? ['00:00', '06:00', '12:00', '18:00'] : ['00:00'];
  for (let m = Math.ceil(begin / 3_600_000) * 3_600_000; m < eind; m += 3_600_000) {
    d.setTime(m);
    if (streep.includes(fmt.format(d))) uit.push(m);
  }
  return uit;
}

const kort = (s, n) => (String(s).length > n ? `${String(s).slice(0, n - 1)}…` : String(s));

/** Eén tunnel in de lijst onder de tekening. */
function Tunnel({ x, dagen }) {
  const { stukken, begin, eind, pct } = uptijd(x, dagen);
  /* Eén regel: stand, naam, kanten, een smalle balk door de tijd en het percentage up; de rest in de tooltip. */
  return (
    <div className="tunnelrij kort" title={[`${x.local.name} ${x.local.ip ?? ''} · ${x.local.interface ?? ''}`, `⇄ ${x.peer.name ?? ''} ${x.peer.ip ?? ''}`,
      t('{n} rekeys', { n: getal(x.syslog.up) }), x.syslog.failed ? t('{n} mislukte onderhandelingen', { n: getal(x.syslog.failed) }) : null].filter(Boolean).join('\n')}>
      <St soort={STAND(x.state)}>{STAND_NAAM(x.state)}</St>
      <span className="tr-naam"><b>{x.topology}</b><span className="muted mono">{x.local.name} ⇄ {x.peer.name ?? x.peer.ip}</span></span>
      <div className="tr-balk" role="img" aria-label={dagen === 1 ? t('Stand door de tijd, de laatste 24 uur') : t('Stand door de tijd, de laatste {n} dagen', { n: dagen })}>
        {stukken.map((st, i) => (
          <span key={i} className={`tr-stuk ${st.stand ? STAND(st.stand) : 'onbekend'}`}
            style={{ left: `${((st.van - begin) / (eind - begin)) * 100}%`, width: `${((st.tot - st.van) / (eind - begin)) * 100}%` }} />
        ))}
        {dagGrenzen(begin, eind, dagen).map((d) => <span key={d} className="tr-dag" style={{ left: `${((d - begin) / (eind - begin)) * 100}%` }} />)}
      </div>
      <span className="mono muted tr-pct">{pct !== null ? `${getal(pct)}%` : '—'}</span>
    </div>
  );
}
